import { sendTracking } from '../../utilities/js/tracking';
import ScrollLock from '../../components/scroll-lock/scroll-lock';
import './modal.scss';

class Modal {
    constructor (element, options) {
        // Defaults & Settings
        const defaults = {
            initAttr: 'data-modal',
            activeAttr: 'data-modal-active',
            autoInitAttr: 'data-modal-init',
            modalAttr: 'data-modaltarget',
            closeButtonAttr: 'data-modal-close-btn',
            exitAttr: 'data-modal-close',
            onClosed: null,
            scrollerAttr: 'scroller',
            targetClassPrefix: null
        };

        this.settings = Object.assign({}, defaults, options);
        this.$root = element;

        // Properties
        this.readOnly = false; // when true, only close/exit is available (used for pre-active modals)

        // Elements
        this.$activeModal = null;
        this.$modalTarget = null;
        this.$closeButton = null;
        this.$$exits = null;
        this.$modalScroller = null;

        if (this.$root.hasAttribute(this.settings.autoInitAttr)) {
            this.readOnly = true;
            this.$modalTarget = this.$root;
            this.modalId = this.$modalTarget.id ?? null;
        } else {
            this.modalId = this.$root.getAttribute(this.settings.initAttr) ??
                (Array.from(this.$root.classList)
                    .find(c => this.settings.targetClassPrefix && c.startsWith(this.settings.targetClassPrefix))
                    ?.replace(this.settings.targetClassPrefix, ''));
            if (this.modalId) {
                this.$modalTarget = document.getElementById(this.modalId);
            }
        }

        this.$closeButton = this.$modalTarget?.querySelector(`[${this.settings.closeButtonAttr}]`);
        this.$$exits = this.$modalTarget?.querySelectorAll(`[${this.settings.exitAttr}]`);

        this.modalOpen = !!this.$modalTarget && this.$modalTarget.hasAttribute(this.settings.activeAttr);

        if (!this.$modalTarget) {
            return;
        }
    }

    initialize () {
        this.setEvents();

        const modalReady = new CustomEvent('modal.ready', {
            detail: { api: this, modalTarget: this.$modalTarget }
        });

        window.dispatchEvent(modalReady);
    }

    openModal () {
        this.$root.setAttribute('aria-pressed', 'true');
        this.$activeModal = document.querySelector(`[${this.settings.activeAttr}]`);

        if (this.$activeModal && this.$activeModal.API) {
            this.$activeModal.API.closeModal();
        }

        this.$modalTarget.setAttribute(this.settings.activeAttr, '');
        this.$modalTarget.removeAttribute('inert');
        this.$root.setAttribute('aria-expanded', 'true');
        this.$modalScroller = this.$modalTarget.querySelector(`[${this.settings.modalAttr}="${this.settings.scrollerAttr}"]`);

        setTimeout(() => {
            this.$closeButton?.focus();
        }, 100);

        if (this.$modalScroller) {
            ScrollLock.disableScroll();
        }

        this.modalOpen = true;

        const modalOpened = new CustomEvent('modal.opened', {
            view: window,
            bubbles: true,
            cancelable: false,
            detail: { $context: this.$modalTarget }
        });

        window.dispatchEvent(modalOpened);
    }

    closeModal () {
        if (!this.modalOpen) return;

        this.modalOpen = false;
        this.$modalTarget.removeAttribute(this.settings.activeAttr);
        this.$modalTarget.setAttribute('inert', '');

        if (this.$modalScroller) {
            ScrollLock.enableScroll();
        }

        if (typeof this.settings.onClosed === 'function') {
            this.settings.onClosed(this.$modalTarget);
        }

        this.$root.setAttribute('aria-pressed', 'false');
        this.$root.setAttribute('aria-expanded', 'false');
        this.$root.focus();

        const modelClosed = new CustomEvent('modal.closed', {
            view: window,
            bubbles: true,
            cancelable: false,
            detail: { $context: this.$modalTarget }
        });

        window.dispatchEvent(modelClosed);
    }

    handleOutsideClick (e) {
        if (this.modalOpen && this.$modalTarget && !this.$modalTarget.contains(e.target)) {
            this.closeModal();
        }
    }

    setEvents () {
        // Only attach root click to open when this instance was created for a trigger element
        if (!this.readOnly) {
            this.$root.addEventListener('click', (e) => {
                e.preventDefault();
                this.openModal();
            });
        }

        this.$closeButton?.addEventListener('keydown', (e) => {
            if (e.code === 'Enter' || e.code === 'Space') {
                e.preventDefault();
                this.closeModal();
            }
        });

        this.$$exits?.forEach($exit => {
            $exit.addEventListener('click', (e) => {
                e.preventDefault();
                this.closeModal();
            });
        });
    }
}

export { Modal };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const targetClassPrefix = 'modal-id-';
        const $$roots = $context.querySelectorAll(
            `[data-modal], [class*="${targetClassPrefix}"]`
        );

        // Select already active modals by default active attribute
        const $$activeModals = $context.querySelectorAll('[data-modal-init]');

        $$roots.forEach($root => {
            const rootAPI = new Modal($root, { targetClassPrefix });
            $root.API = rootAPI;

            if (rootAPI.$modalTarget) {
                rootAPI.$modalTarget.API = rootAPI;
            }

            rootAPI.initialize();
        });

        // Initialize API for already active modals to enable closing behavior
        $$activeModals.forEach($modalEl => {
            if ($modalEl.API) {
                return;
            }
            const modalAPI = new Modal($modalEl, { targetClassPrefix });
            $modalEl.API = modalAPI;
            modalAPI.initialize();
        });
    }
});
